import type * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn.ts';
import type { ChatAuthor } from './chat-message.tsx';

export type ChatTurnProps = Omit<React.ComponentProps<'div'>, 'style'>;

/**
 * One message with what hangs under it: the line saying when it was said, and
 * any notes about it. A read transcript reveals a turn's tools on hover, so the
 * turn is the group they answer to.
 */
export function ChatTurn({ className, ...props }: ChatTurnProps) {
  return (
    <div
      data-slot='chat-turn'
      className={cn('group/turn flex flex-col gap-1', className)}
      {...props}
    />
  );
}

export interface ChatMessageMetaProps extends Omit<React.ComponentProps<'div'>, 'style'> {
  /** Right under the user's words, left under Wundo's, in line with the bubble. */
  author: ChatAuthor;
  /** Ways out of the message, such as a trace link: hidden until the turn is hovered or focused. */
  tools?: React.ReactNode;
  /** Shows the tools without a hover, which is how a touch screen asks for them. */
  toolsShown?: boolean;
}

/**
 * The quiet line under a message: when it was said and what became of it.
 *
 * Everything else a reader may need waits until asked for, so a transcript
 * reads as the conversation and not as a row of icons under every turn.
 */
export function ChatMessageMeta({
  author,
  tools,
  toolsShown = false,
  className,
  children,
  ...props
}: ChatMessageMetaProps) {
  return (
    <div
      data-slot='chat-message-meta'
      className={cn(
        'flex min-h-7 flex-wrap items-center gap-x-1.5 text-xs text-ink-muted',
        author === 'user' ? 'justify-end' : 'pl-9',
        className,
      )}
      {...props}
    >
      {children}
      {tools ? (
        <span
          data-slot='chat-message-tools'
          data-shown={toolsShown}
          className={cn(
            'inline-flex items-center gap-0.5 transition-opacity',
            toolsShown
              ? 'opacity-100'
              : 'opacity-0 group-focus-within/turn:opacity-100 group-hover/turn:opacity-100 pointer-coarse:hidden',
          )}
        >
          {tools}
        </span>
      ) : null}
    </div>
  );
}

const chatNoteVariants = cva(
  'border-l-2 py-0.5 pl-2.5 text-xs leading-normal whitespace-pre-wrap',
  {
    variants: {
      tone: {
        danger: 'border-danger text-ink-secondary',
        warning: 'border-on-pink text-ink-secondary',
        info: 'border-primary text-ink-secondary',
        neutral: 'border-line text-ink-body',
      },
      author: {
        user: 'ml-auto w-4/5',
        wundo: 'ml-9 max-w-[calc(80%-2.25rem)]',
      },
    },
    defaultVariants: { tone: 'neutral', author: 'wundo' },
  },
);

const chatNoteLabelVariants = cva('font-bold', {
  variants: {
    tone: {
      danger: 'text-danger',
      warning: 'text-on-pink',
      info: 'text-primary',
      neutral: 'text-ink',
    },
  },
  defaultVariants: { tone: 'neutral' },
});

export interface ChatNoteProps
  extends
    Omit<React.ComponentProps<'div'>, 'style'>,
    VariantProps<typeof chatNoteVariants> {
  author: ChatAuthor;
  /** A word or two in bold that says what kind of note this is. */
  label?: React.ReactNode;
}

/**
 * A remark about one message, set under it on the message's side: a light
 * rule and small type, so it reads as a margin note and never as another turn.
 */
export function ChatNote({
  author,
  tone,
  label,
  className,
  children,
  ...props
}: ChatNoteProps) {
  return (
    <div
      data-slot='chat-note'
      data-tone={tone ?? 'neutral'}
      className={cn(chatNoteVariants({ tone, author }), className)}
      {...props}
    >
      {label ? (
        <>
          <span className={chatNoteLabelVariants({ tone })}>{label}</span> ·{' '}
        </>
      ) : null}
      {children}
    </div>
  );
}

export type ChatDividerProps = Omit<React.ComponentProps<'div'>, 'style'>;

/** Where one conversation ends and the next begins, in a transcript read as one. */
export function ChatDivider({ className, ...props }: ChatDividerProps) {
  return (
    <div
      data-slot='chat-divider'
      className={cn(
        'flex items-center gap-3 py-2 text-xs font-semibold text-ink-muted',
        'before:h-px before:flex-1 before:bg-line after:h-px after:flex-1 after:bg-line',
        className,
      )}
      {...props}
    />
  );
}

export { chatNoteVariants };
